import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { readOnlyMessage } from './readOnlyNotice';
import type { Arbeitsschritt, SigeProject } from '../components/sige-plan/types';
import type {
    KoordinierungPaar,
    NeuesKoordinierungPaar,
} from '../components/koordinierung/koordinierungLogic';

/**
 * Datenbankzugriff des Moduls "Gewerke-Ueberschneidung" (Tabelle
 * `koordinierung_paare` — supabase/handbuch_koordinierung.sql, MIGRATIONS.md
 * Nr. 40) und das Lesen der Planquelle. Die einzige Stelle, die mit ihnen redet.
 *
 * Die REGELN liegen daneben in `components/koordinierung/koordinierungLogic.ts`
 * und sind ohne Datenbank pruefbar; hier steht nur, was ohne Supabase nicht zu
 * haben ist. Muster und Hausregeln wortgleich aus `planungsphaseService.ts`:
 *
 * (a) LESEN IST FAIL-OPEN, SCHREIBEN NICHT. Migration Nr. 40 ist noch nicht
 *     gefahren; fehlt die Tabelle (42P01 / PGRST205), liefert das Lesen eine
 *     leere Lage statt zu werfen. Die Kachel oeffnet dann mit der gerechneten
 *     Analyse und ohne gespeicherte Bewertungen — sie stirbt nicht. Jeder
 *     schreibende Aufruf meldet den Fehler dagegen sichtbar.
 *
 * (b) KEIN LESEN-DANN-SCHREIBEN. Wo eine Bedingung gilt (nur einmal
 *     bestaetigen, nur einmal veralten), steht sie als `where`-Bedingung IM
 *     UPDATE und nicht als vorheriges `select`. Zwischen Lesen und Schreiben
 *     passt eine zweite Sitzung.
 *
 * (c) ZWEITE REIHE SCHREIBSCHUTZ. Jeder schreibende Aufruf verlangt einen
 *     `Schreibzugriff` und prueft `isReadOnly` selbst. Das ist kein Guertel und
 *     Hosentraeger: das Modul haengt an einer eigenen Tabelle, hinter der kein
 *     `handleUpdateReport`-Wachposten steht, und `isReadOnly` kann zur Laufzeit
 *     kippen, wenn die Projektsperre an einen anderen Benutzer geht.
 *
 * ─── Was hier NICHT passiert ──────────────────────────────────────────────
 * Kein Schreibzugriff auf `sige_*`. Die Koordinierung LIEST den Plan und legt
 * ihre Bewertung daneben ab; sie aendert keinen Arbeitsschritt, keinen
 * Zeitraum und keine Gefaehrdung des SiGe-Plans.
 */

const T_PAARE = 'koordinierung_paare';

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};
const istDublette = (e: unknown): boolean => errorCode(e) === '23505';

/**
 * Der Schreibschutz-Nachweis, den jeder schreibende Aufruf mitbringen muss.
 *
 * Ausdruecklich ein Pflichtparameter und kein Vorgabewert `false`: ein
 * vergessener Parameter soll den Uebersetzer stoeren und nicht still den
 * Schreibweg oeffnen.
 */
export interface Schreibzugriff {
    isReadOnly: boolean;
    /** Anzeigename des Handelnden — wandert bei der Bestaetigung mit. */
    benutzerName?: string;
}

const pruefeSchreibrecht = (zugriff: Schreibzugriff, was: string): void => {
    if (zugriff && zugriff.isReadOnly) throw new Error(readOnlyMessage(was));
};

/**
 * Vorgaben fuer Spalten, die die Datenbank NULL-bar haelt, der Client aber nie
 * NULL sehen soll — sonst steht im Textfeld "null".
 */
const mapPaar = (row: Record<string, unknown>): KoordinierungPaar => ({
    ...(row as unknown as KoordinierungPaar),
    gewerk_a_label: typeof row.gewerk_a_label === 'string' ? row.gewerk_a_label : '',
    gewerk_b_label: typeof row.gewerk_b_label === 'string' ? row.gewerk_b_label : '',
    gefaehrdung: typeof row.gefaehrdung === 'string' ? row.gefaehrdung : '',
    massnahme: typeof row.massnahme === 'string' ? row.massnahme : '',
    prioritaet: (row.prioritaet === 'hoch' || row.prioritaet === 'niedrig'
        ? row.prioritaet
        : 'mittel') as KoordinierungPaar['prioritaet'],
    quelle: (row.quelle === 'ki' || row.quelle === 'hand'
        ? row.quelle
        : 'regel') as KoordinierungPaar['quelle'],
    dauer_tage: typeof row.dauer_tage === 'number' ? row.dauer_tage : null,
});

/**
 * Spalten, die der Client NIE mitschickt. `updated_at` kommt vom Trigger, die
 * Identitaeten und Fremdschluessel stehen fest — ein Update, das sie
 * mitschickt, kann eine Zeile in ein fremdes Projekt umhaengen.
 *
 * `gewerk_a_norm`/`gewerk_b_norm` gehoeren mit dazu: sie sind der Schluessel
 * des Paares. Wer sie nachtraeglich aendert, haengt eine Bewertung an eine
 * andere Paarung um, ohne dass es jemand sieht.
 */
const GESPERRTE_SPALTEN = new Set([
    'id', 'project_id', 'created_at', 'updated_at',
    'gewerk_a_norm', 'gewerk_b_norm',
]);

const ohneGesperrteSpalten = <T extends object>(updates: T): Record<string, unknown> => {
    const rein: Record<string, unknown> = {};
    Object.entries(updates as Record<string, unknown>).forEach(([k, v]) => {
        if (GESPERRTE_SPALTEN.has(k)) return;
        // Leerstring bei Datums- und Zeitstempelspalten heisst "keine Angabe"
        // und muss als NULL ankommen — PostgREST wuerde '' sonst als Datum
        // parsen wollen und mit 22007 abbrechen.
        if (v === '' && (k.endsWith('_am') || k.endsWith('_von') || k.endsWith('_bis') || k.endsWith('_seit'))) {
            rein[k] = null;
        } else rein[k] = v;
    });
    return rein;
};

// ═══════════════════════════════════════════════════════════════════════════
// PLANQUELLE
// ═══════════════════════════════════════════════════════════════════════════

export interface PlanQuelle {
    sigeProject: SigeProject | null;
    arbeitsschritte: Arbeitsschritt[];
}

const LEERE_QUELLE: PlanQuelle = { sigeProject: null, arbeitsschritte: [] };

/**
 * PostgREST deckelt bei 1000 Zeilen OHNE MELDUNG. Ein Plotterplan mit ueber
 * tausend Arbeitsschritten ist selten, aber die stille Kappung faellt
 * ausgerechnet dort nicht auf, wo sie am meisten kostet: fehlende Schritte
 * heissen fehlende Gewerke, und ein fehlendes Gewerk heisst eine
 * Ueberschneidung, die niemand sieht.
 */
const SEITE = 1000;

/**
 * Liest die NEUESTE, nicht geloeschte Planversion des Projekts samt
 * Arbeitsschritten.
 *
 * Die Auswahl der Fassung ist ZEICHENGLEICH zum Vorbild `ladePlanQuelle` in
 * `src/components/sige-einweisung/useEinweisungen.ts:98-142`: `project_id`,
 * `deleted_at is null`, `created_at` absteigend, `limit(1)`. Das
 * `.is('deleted_at', null)` ist tragend — `sige_projekte` loescht WEICH
 * (`versioning.ts:31`, `deleted_at` steht in keinem TS-Interface), und ohne den
 * Filter bewertete die Koordinierung womoeglich eine verworfene Fassung.
 *
 * ABWEICHUNG vom Vorbild: die drei Zweige `sige_gefaehrdungen`,
 * `sige_schutzmassnahmen` und `sige_bloecke` werden NICHT gelesen. Die
 * Ueberschneidung entsteht allein aus `gewerk` und `zeitraum_von/bis` am
 * Arbeitsschritt; drei zusaetzliche Abfragen brauchte niemand, und auf einer
 * schmalen Baustellenleitung ist das spuerbar. Die Spaltennamen stammen aus
 * `src/components/sige-plan/types.ts` — es gibt fuer diese Tabellen kein SQL im
 * Repo, geraten wird hier nichts.
 */
export const ladeKoordinierungsQuelle = async (projectId: string): Promise<PlanQuelle> => {
    if (!projectId || projectId === 'temp-id') return LEERE_QUELLE;

    const { data: planListe, error: planFehler } = await supabase
        .from('sige_projekte')
        .select('*')
        .eq('project_id', projectId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1);
    if (planFehler) throw new Error(describeError(planFehler));

    const plan = ((planListe || [])[0] as SigeProject | undefined) ?? null;
    if (!plan) return LEERE_QUELLE;

    const arbeitsschritte: Arbeitsschritt[] = [];
    for (let von = 0; ; von += SEITE) {
        const { data, error } = await supabase
            .from('sige_arbeitsschritte')
            .select('*')
            .eq('sige_projekt_id', plan.id)
            .order('sortierung', { ascending: true })
            // Zweites Kriterium: `sortierung` allein ist nicht eindeutig, und
            // eine uneindeutige Sortierung laesst beim Blaettern Zeilen doppelt
            // erscheinen oder ganz ausfallen.
            .order('id', { ascending: true })
            .range(von, von + SEITE - 1);
        if (error) throw new Error(describeError(error));
        const seite = (data || []) as Arbeitsschritt[];
        arbeitsschritte.push(...seite);
        if (seite.length < SEITE) break;
    }

    return { sigeProject: plan, arbeitsschritte };
};

// ═══════════════════════════════════════════════════════════════════════════
// BEWERTETE PAARE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Alle bewerteten Paare eines Projekts — auch die veralteten.
 *
 * Veraltete Zeilen werden ausdruecklich MITGELESEN. Sie auszufiltern waere
 * dasselbe wie sie zu loeschen, nur unsichtbar: der Koordinator saehe nicht
 * mehr, dass eine Gefaehrdung einmal bewertet war, und legte sie beim
 * naechsten Terminstand ein zweites Mal an.
 *
 * Wirft NICHT bei fehlender Tabelle (siehe (a) im Dateikopf).
 */
const ladePaare = async (
    projectId: string,
): Promise<{ paare: KoordinierungPaar[]; tabelleFehlt: boolean }> => {
    if (!projectId || projectId === 'temp-id') return { paare: [], tabelleFehlt: false };
    const alle: KoordinierungPaar[] = [];
    for (let von = 0; ; von += SEITE) {
        const { data, error } = await supabase
            .from(T_PAARE)
            .select('*')
            .eq('project_id', projectId)
            .order('gewerk_a_norm', { ascending: true })
            .order('gewerk_b_norm', { ascending: true })
            .order('id', { ascending: true })
            .range(von, von + SEITE - 1);
        if (error) {
            if (istFehlendeTabelle(error)) return { paare: [], tabelleFehlt: true };
            throw new Error(describeError(error));
        }
        const seite = data || [];
        alle.push(...seite.map(mapPaar));
        if (seite.length < SEITE) return { paare: alle, tabelleFehlt: false };
    }
};

export const fetchKoordinierungPaare = async (projectId: string): Promise<KoordinierungPaar[]> =>
    (await ladePaare(projectId)).paare;

export interface KoordinierungsLage {
    quelle: PlanQuelle;
    paare: KoordinierungPaar[];
    /** `true`, wenn Migration Nr. 40 noch nicht gefahren ist. Die Oberflaeche
     *  sagt das dann ausdruecklich, statt "keine Bewertungen" zu behaupten. */
    tabelleFehlt: boolean;
}

/**
 * Alles auf einmal. Legt NICHTS an — ein blosses Oeffnen der Kachel soll keinen
 * Datensatz erzeugen. Dieselbe Ueberlegung wie beim Schreibschutz fuer
 * Nur-Ansicht-Projekte, wo Selbstheilungs-Migrationen beim reinen Lesen
 * geschrieben hatten.
 */
export const fetchKoordinierungsLage = async (projectId: string): Promise<KoordinierungsLage> => {
    // Beide Zweige laufen nebeneinander: die Planquelle liegt in `sige_*`, die
    // Bewertungen in `koordinierung_paare` — es gibt keine Abhaengigkeit
    // zwischen ihnen, und nacheinander waeren es zwei Rundreisen statt einer.
    const [quelle, bewertungen] = await Promise.all([
        ladeKoordinierungsQuelle(projectId),
        ladePaare(projectId),
    ]);
    // `tabelleFehlt` faellt beim Lesen mit ab — der Unterschied zaehlt: "keine
    // Bewertungen erfasst" und "die Migration ist noch nicht gelaufen" sehen im
    // Client gleich aus, meinen aber Gegenteiliges. Im ersten Fall ist alles in
    // Ordnung, im zweiten ginge jeder Klick auf „Bestätigen" ins Leere.
    return { quelle, paare: bewertungen.paare, tabelleFehlt: bewertungen.tabelleFehlt };
};

/**
 * Legt eine Bewertung an.
 *
 * `upsert` mit `ignoreDuplicates` statt `insert`: der Unique-Index
 * (project_id, gewerk_a_norm, gewerk_b_norm, gefaehrdung) ist der Riegel gegen
 * dieselbe Gefaehrdung zweimal am selben Paar. Ein zweiter Klick auf
 * "Vorschlag uebernehmen" soll nicht mit 23505 abbrechen, sondern schlicht
 * nichts zusaetzlich anlegen — und die vorhandene Zeile zurueckgeben, damit die
 * Oberflaeche sie zeigt statt zu behaupten, es sei nichts passiert.
 */
export const createKoordinierungPaar = async (
    projectId: string,
    entwurf: NeuesKoordinierungPaar,
    zugriff: Schreibzugriff,
): Promise<KoordinierungPaar> => {
    pruefeSchreibrecht(zugriff, 'eine Bewertung zur Gewerke-Überschneidung anzulegen');
    if (!projectId || projectId === 'temp-id') {
        throw new Error('Ohne gespeichertes Projekt kann keine Bewertung angelegt werden.');
    }
    if (!entwurf || !String(entwurf.gefaehrdung || '').trim()) {
        throw new Error('Eine Bewertung ohne benannte Gefährdung ist keine Bewertung.');
    }
    if (!(entwurf.gewerk_a_norm < entwurf.gewerk_b_norm)) {
        // Denselben Riegel traegt die Datenbank als CHECK. Hier bricht er mit
        // einem lesbaren Satz ab statt mit 23514.
        throw new Error('Die beiden Gewerke eines Paares müssen verschieden und geordnet sein (a < b).');
    }

    const zeile = { ...entwurf, project_id: projectId };
    const { data, error } = await supabase
        .from(T_PAARE)
        .upsert([zeile], {
            onConflict: 'project_id,gewerk_a_norm,gewerk_b_norm,gefaehrdung',
            ignoreDuplicates: true,
        })
        .select('*');
    if (error) throw new Error(describeError(error));

    const angelegt = (data || [])[0];
    if (angelegt) return mapPaar(angelegt);

    // `ignoreDuplicates` liefert bei einem Treffer keine Zeile zurueck — die
    // vorhandene wird nachgelesen.
    const { data: bestand, error: leseFehler } = await supabase
        .from(T_PAARE)
        .select('*')
        .eq('project_id', projectId)
        .eq('gewerk_a_norm', entwurf.gewerk_a_norm)
        .eq('gewerk_b_norm', entwurf.gewerk_b_norm)
        .eq('gefaehrdung', entwurf.gefaehrdung)
        .limit(1);
    if (leseFehler) throw new Error(describeError(leseFehler));
    const vorhanden = (bestand || [])[0];
    if (vorhanden) return mapPaar(vorhanden);
    throw new Error('Die Bewertung konnte weder angelegt noch gelesen werden.');
};

export const updateKoordinierungPaar = async (
    id: string,
    updates: Partial<KoordinierungPaar>,
    zugriff: Schreibzugriff,
): Promise<KoordinierungPaar> => {
    pruefeSchreibrecht(zugriff, 'eine Bewertung zu ändern');
    const rein = ohneGesperrteSpalten(updates);
    if (!Object.keys(rein).length) throw new Error('Es gibt nichts zu speichern.');
    const { data, error } = await supabase
        .from(T_PAARE)
        .update(rein)
        .eq('id', id)
        .select('*')
        .single();
    if (error) {
        if (istDublette(error)) {
            throw new Error('Zu diesem Gewerkepaar gibt es diese Gefährdung bereits.');
        }
        throw new Error(describeError(error));
    }
    return mapPaar(data);
};

/**
 * Bestaetigt eine Bewertung — der Schritt, der aus dem Vorschlag eine
 * Festlegung des Koordinators macht.
 *
 * Der Guard `is('bestaetigt_am', null)` steht IM UPDATE (siehe (b)): wird
 * derselbe Knopf zweimal gedrueckt, bleibt das ERSTE Bestaetigungsdatum
 * stehen. Das ist Absicht — im Streitfall zaehlt, wann zum ersten Mal
 * festgestellt wurde, nicht wann zuletzt geklickt wurde.
 *
 * Gibt `false` zurueck, wenn bereits bestaetigt war.
 */
export const bestaetigeKoordinierungPaar = async (
    id: string,
    jetztIso: string,
    zugriff: Schreibzugriff,
): Promise<boolean> => {
    pruefeSchreibrecht(zugriff, 'eine Bewertung zu bestätigen');
    const name = String((zugriff && zugriff.benutzerName) || '').trim();
    if (!name) {
        // Eine Bestaetigung ohne Namen ist keine. Sie steht spaeter im PDF als
        // Feststellung des Koordinators und braucht den Handelnden.
        throw new Error('Für die Bestätigung fehlt der Name des Koordinators.');
    }
    const { data, error } = await supabase
        .from(T_PAARE)
        .update({ bestaetigt_am: jetztIso, bestaetigt_von_name: name })
        .eq('id', id)
        .is('bestaetigt_am', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length > 0;
};

/** Nimmt eine Bestaetigung zurueck. Der Text der Bewertung bleibt stehen —
 *  zurueckgenommen wird die Feststellung, nicht die Erkenntnis. */
export const widerrufeBestaetigung = async (
    id: string,
    zugriff: Schreibzugriff,
): Promise<void> => {
    pruefeSchreibrecht(zugriff, 'eine Bestätigung zurückzunehmen');
    const { error } = await supabase
        .from(T_PAARE)
        .update({ bestaetigt_am: null, bestaetigt_von_name: null })
        .eq('id', id);
    if (error) throw new Error(describeError(error));
};

/**
 * Markiert Bewertungen als veraltet — die Ueberschneidung, auf die sie sich
 * bezogen, gibt der Plan nicht mehr her.
 *
 * ═══ HIER WIRD NICHTS GELOESCHT ══════════════════════════════════════════
 * Das ist der Kern der ganzen Ablage: die Ueberschneidung ist jederzeit neu
 * rechenbar, die BEWERTUNG ist es nicht. Eine geloeschte Zeile hiesse
 * hinterher, die Gefaehrdung habe es nie gegeben — dabei hat der Koordinator
 * sie erkannt, geregelt und der Bauherr hat danach den Termin verschoben.
 * `veraltet_seit` haelt genau das fest: bis wann sie galt.
 *
 * Der Guard `is('veraltet_seit', null)` steht IM UPDATE: ein zweiter Lauf
 * verschiebt das Datum nicht.
 *
 * Gibt die Zahl der tatsaechlich gestempelten Zeilen zurueck.
 */
export const markiereVeraltet = async (
    ids: string[],
    jetztIso: string,
    zugriff: Schreibzugriff,
): Promise<number> => {
    pruefeSchreibrecht(zugriff, 'überholte Bewertungen zu kennzeichnen');
    if (!ids || !ids.length) return 0;
    const { data, error } = await supabase
        .from(T_PAARE)
        .update({ veraltet_seit: jetztIso })
        .in('id', ids)
        .is('veraltet_seit', null)
        .select('id');
    if (error) {
        // Fail-open ist hier bewusst NICHT vorgesehen: das Kennzeichnen ist ein
        // Schreibweg, und ein stiller Fehlschlag hinterliesse eine Liste, die
        // "alles aktuell" behauptet.
        throw new Error(describeError(error));
    }
    return (data || []).length;
};

/** Hebt die Veralterung auf — die Ueberschneidung ist zurueck. Die Bewertung
 *  gilt damit wieder; ihr Text bleibt unveraendert. */
export const hebeVeralterungAuf = async (
    ids: string[],
    zugriff: Schreibzugriff,
): Promise<number> => {
    pruefeSchreibrecht(zugriff, 'die Kennzeichnung „überholt" aufzuheben');
    if (!ids || !ids.length) return 0;
    const { data, error } = await supabase
        .from(T_PAARE)
        .update({ veraltet_seit: null })
        .in('id', ids)
        .not('veraltet_seit', 'is', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length;
};

/**
 * Uebernimmt das gerechnete Fenster in eine Zeile, deren Terminstand sich
 * verschoben hat.
 *
 * Ausdruecklich ein eigener, angestossener Weg und kein Automatismus beim
 * Laden: die Bewertung bezog sich auf den ALTEN Zeitraum. Ob sie fuer den neuen
 * noch gilt, entscheidet der Koordinator — und mit dieser Entscheidung faellt
 * auch eine etwaige Bestaetigung, denn bestaetigt wurde etwas anderes.
 */
export const uebernimmNeuesFenster = async (
    id: string,
    fenster: { von: string | null; bis: string | null; dauerTage: number | null },
    zugriff: Schreibzugriff,
): Promise<KoordinierungPaar> => {
    pruefeSchreibrecht(zugriff, 'den geänderten Zeitraum zu übernehmen');
    const { data, error } = await supabase
        .from(T_PAARE)
        .update({
            ueberschneidung_von: fenster.von || null,
            ueberschneidung_bis: fenster.bis || null,
            dauer_tage: typeof fenster.dauerTage === 'number' ? fenster.dauerTage : null,
            bestaetigt_am: null,
            bestaetigt_von_name: null,
        })
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapPaar(data);
};

/**
 * Loescht eine Bewertung.
 *
 * NUR eine unbestaetigte. Der Guard `is('bestaetigt_am', null)` steht in der
 * Abfrage: eine bestaetigte Bewertung ist eine Feststellung des Koordinators
 * und verschwindet nicht auf Knopfdruck — wer sie loswerden will, nimmt zuerst
 * die Bestaetigung zurueck und trifft damit eine sichtbare Entscheidung.
 *
 * Gibt `false` zurueck, wenn nichts geloescht wurde.
 */
export const deleteKoordinierungPaar = async (
    id: string,
    zugriff: Schreibzugriff,
): Promise<boolean> => {
    pruefeSchreibrecht(zugriff, 'eine Bewertung zu löschen');
    const { data, error } = await supabase
        .from(T_PAARE)
        .delete()
        .eq('id', id)
        .is('bestaetigt_am', null)
        .select('id');
    if (error) throw new Error(describeError(error));
    return (data || []).length > 0;
};
